"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Radio, Users, Lock, Clock, Shield } from "lucide-react";

export default function LiveListPage() {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/live")
      .then((r) => r.json())
      .then((data) => setSessions(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-neutral-500 text-sm font-mono uppercase tracking-widest animate-pulse">Loading...</div>
      </main>
    );
  }

  const liveSessions = sessions.filter((s) => s.status === "LIVE");
  const scheduled = sessions.filter((s) => s.status === "SCHEDULED");

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-6xl mx-auto px-4 py-10">

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tighter mb-1">Live Now</h1>
            <p className="text-neutral-500 text-sm">{liveSessions.length} active sessions</p>
          </div>
        </div>

        {/* LIVE NOW */}
        {liveSessions.length === 0 && scheduled.length === 0 ? (
          <div className="text-center py-20">
            <Radio className="w-12 h-12 text-neutral-800 mx-auto mb-4" />
            <p className="text-neutral-500 mb-2">No one is live right now</p>
            <p className="text-neutral-600 text-sm">Check back later or browse recorded content</p>
            <Link href="/browse" className="text-red-500 text-sm font-bold mt-4 inline-block">Browse content</Link>
          </div>
        ) : (
          <>
            {liveSessions.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
                {liveSessions.map((s) => (
                  <Link key={s.id} href={`/live/${s.id}`} className="group bg-[#0a0a0a] border border-neutral-800/60 rounded-xl overflow-hidden hover:border-red-600/30 transition-all">
                    {/* Fake video preview area */}
                    <div className="aspect-video bg-neutral-900 relative flex items-center justify-center">
                      <div className="w-full h-full bg-gradient-to-br from-red-900/10 to-neutral-900 flex items-center justify-center">
                        <Radio className="w-10 h-10 text-red-600/40" />
                      </div>

                      {/* LIVE badge */}
                      <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600 px-2 py-0.5 rounded text-[10px] font-black text-white uppercase tracking-wider">
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        LIVE
                      </div>

                      {/* Viewer count */}
                      <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/70 px-2 py-0.5 rounded text-[10px] font-bold text-white">
                        <Users className="w-3 h-3" /> {s.viewerCount || 0}
                      </div>

                      {/* Private badge */}
                      {s.isPrivate && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 bg-yellow-500/90 text-black px-2 py-0.5 rounded text-[9px] font-black uppercase">
                          <Lock className="w-2.5 h-2.5" /> {s.ticketPrice ? `${s.ticketPrice} CR` : "Subs only"}
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <h3 className="font-bold text-white text-sm group-hover:text-red-500 transition line-clamp-1">{s.title}</h3>
                      <div className="flex items-center gap-2 mt-2">
                        {s.host.avatarUrl && (
                          <img src={s.host.avatarUrl} alt="" className="w-5 h-5 rounded-full object-cover border border-neutral-700" />
                        )}
                        <span className="text-[11px] font-bold text-neutral-400">{s.host.username}</span>
                        {s.host.isVerified && <Shield className="w-3 h-3 text-blue-500" />}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* SCHEDULED */}
            {scheduled.length > 0 && (
              <>
                <h2 className="text-lg font-black uppercase tracking-wider text-neutral-300 mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-neutral-500" /> Scheduled
                </h2>
                <div className="space-y-2">
                  {scheduled.map((s) => (
                    <div key={s.id} className="flex items-center gap-4 p-4 bg-[#0a0a0a] border border-neutral-800/60 rounded-lg">
                      {s.host.avatarUrl && (
                        <img src={s.host.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover border border-neutral-700" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white text-sm truncate">{s.title}</h3>
                        <div className="text-[11px] text-neutral-500 mt-0.5">
                          {s.host.username} — {s.scheduledAt ? new Date(s.scheduledAt).toLocaleString() : "TBD"}
                        </div>
                      </div>
                      {s.isPrivate && (
                        <span className="text-[9px] font-bold text-yellow-500 bg-yellow-900/20 px-2 py-1 rounded uppercase">Subs only</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
